import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore, initAuthListener } from '@/store/authStore';
import { AppShell, PlatformShell } from '@/components/layout';
import { LoadingScreen } from '@/components/ui';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleGate } from '@/components/auth/RoleGate';
import { PlatformProtectedRoute } from '@/components/auth/PlatformProtectedRoute';
import { PlatformRoleGate } from '@/components/auth/PlatformRoleGate';
import AuthLayout from '@/components/auth/AuthLayout';

// Lazy load pages for code splitting, matching the B2C reference app's
// convention. Routes not yet built by a completed user story point at the
// temporary ComingSoonPage placeholder (see tasks.md T037); each is
// replaced with its real page component as that story's phase lands.
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));

// User Story 1 — Sign in and see what needs my attention
const PersonalSettingsPage = lazy(() => import('@/pages/settings/PersonalSettingsPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const MfaVerifyPage = lazy(() => import('@/pages/auth/MfaVerifyPage'));
const RequestAccessPage = lazy(() => import('@/pages/auth/RequestAccessPage'));
const AccessDeniedPage = lazy(() => import('@/pages/error/AccessDeniedPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));

// User Story 2 — Ask DECIXA
const ChatPage = lazy(() => import('@/pages/chat/ChatPage'));

// User Story 3 — Decision Check
const CreateDecisionPage = lazy(() => import('@/pages/decision/CreateDecisionPage'));
const DecisionCheckListPage = lazy(() => import('@/pages/decision/DecisionCheckListPage'));
const DecisionDetailPage = lazy(() => import('@/pages/decision/DecisionDetailPage'));

// User Story 4 — Approvals
const MyApprovalsPage = lazy(() => import('@/pages/approvals/MyApprovalsPage'));

// User Story 5 — Policy Navigator
const PolicyLibraryPage = lazy(() => import('@/pages/policies/PolicyLibraryPage'));
const PolicyDetailPage = lazy(() => import('@/pages/policies/PolicyDetailPage'));

// User Story 6 — Compliance Review
const ComplianceReviewListPage = lazy(() => import('@/pages/compliance/ComplianceReviewListPage'));
const NewComplianceReviewPage = lazy(() => import('@/pages/compliance/NewComplianceReviewPage'));
const ComplianceReviewDetailPage = lazy(() => import('@/pages/compliance/ComplianceReviewDetailPage'));

// User Story 7 — Approvals Matrix (DoA)
const DoaOverviewPage = lazy(() => import('@/pages/doa/DoaOverviewPage'));
const DoaMatrixDetailPage = lazy(() => import('@/pages/doa/DoaMatrixDetailPage'));
const DoaMatrixEditorPage = lazy(() => import('@/pages/doa/DoaMatrixEditorPage'));
const DoaUploadPage = lazy(() => import('@/pages/doa/DoaUploadPage'));
const DoaExtractionReviewPage = lazy(() => import('@/pages/doa/DoaExtractionReviewPage'));

// User Story 8 — Templates
const TemplateLibraryPage = lazy(() => import('@/pages/templates/TemplateLibraryPage'));
const TemplateGeneratorPage = lazy(() => import('@/pages/templates/TemplateGeneratorPage'));
const GeneratedDocumentPage = lazy(() => import('@/pages/templates/GeneratedDocumentPage'));

// User Story 9 — Admin
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage'));
const AddUserPage = lazy(() => import('@/pages/admin/AddUserPage'));
const EditUserPage = lazy(() => import('@/pages/admin/EditUserPage'));
const DepartmentManagementPage = lazy(() => import('@/pages/admin/DepartmentManagementPage'));
const PolicyManagementPage = lazy(() => import('@/pages/admin/PolicyManagementPage'));
const TemplateManagementPage = lazy(() => import('@/pages/admin/TemplateManagementPage'));
const DocumentManagementPage = lazy(() => import('@/pages/admin/DocumentManagementPage'));
const OrganisationSettingsPage = lazy(() => import('@/pages/admin/OrganisationSettingsPage'));
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'));
const ErrorDashboardPage = lazy(() => import('@/pages/admin/ErrorDashboardPage'));
const AccessRequestsPage = lazy(() => import('@/pages/admin/AccessRequestsPage'));

// Platform Admin — cross-organisation Super Admin / Co-Admin area
const PlatformDashboardPage = lazy(() => import('@/pages/platform/PlatformDashboardPage'));
const OrganisationsListPage = lazy(() => import('@/pages/platform/OrganisationsListPage'));
const AddOrganisationPage = lazy(() => import('@/pages/platform/AddOrganisationPage'));
const InviteOrgAdminPage = lazy(() => import('@/pages/platform/InviteOrgAdminPage'));
const AdminsManagementPage = lazy(() => import('@/pages/platform/AdminsManagementPage'));

function App() {
  useEffect(() => {
    // Syncs the auth store with the Supabase session lifecycle (expiry,
    // sign-out in other tabs, token refresh, stale persisted state).
    initAuthListener();
  }, []);

  const RootRedirect = () => {
    const { isAuthenticated, platformAdmin } = useAuthStore();
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return <Navigate to={platformAdmin ? '/platform' : '/dashboard'} replace />;
  };

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Root: redirect based on auth state */}
        <Route path="/" element={<RootRedirect />} />

        {/* ============================================
            AUTH ROUTES - nested under AuthLayout
            Static branding panel, only the form content changes
            ============================================ */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Standalone auth-adjacent routes */}
        <Route path="/mfa-verify" element={<MfaVerifyPage />} />
        <Route path="/request-access" element={<RequestAccessPage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />

        {/* ============================================
            PROTECTED ROUTES - requires authentication + active org membership
            ============================================ */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationId" element={<ChatPage />} />

          <Route path="/decisions" element={<DecisionCheckListPage />} />
          <Route path="/decisions/new" element={<CreateDecisionPage />} />
          <Route path="/decisions/:id/edit" element={<CreateDecisionPage />} />
          <Route path="/decisions/:id" element={<DecisionDetailPage />} />

          <Route path="/approvals" element={<MyApprovalsPage />} />

          <Route path="/policies" element={<PolicyLibraryPage />} />
          <Route path="/policies/:id" element={<PolicyDetailPage />} />

          <Route path="/compliance" element={<ComplianceReviewListPage />} />
          <Route path="/compliance/new" element={<NewComplianceReviewPage />} />
          <Route path="/compliance/:id" element={<ComplianceReviewDetailPage />} />

          <Route path="/templates" element={<TemplateLibraryPage />} />
          <Route path="/templates/:id" element={<TemplateGeneratorPage />} />
          <Route path="/templates/generated/:id" element={<GeneratedDocumentPage />} />

          <Route path="/doa" element={<DoaOverviewPage />} />
          <Route path="/doa/upload" element={<RoleGate role="admin"><DoaUploadPage /></RoleGate>} />
          <Route path="/doa/review/:matrixId" element={<RoleGate role="admin"><DoaExtractionReviewPage /></RoleGate>} />
          <Route path="/doa/:id" element={<DoaMatrixDetailPage />} />
          <Route path="/doa/:id/edit" element={<RoleGate role="admin"><DoaMatrixEditorPage /></RoleGate>} />

          <Route path="/settings" element={<PersonalSettingsPage />} />

          {/* Admin — role-gated below Admin/Owner (FR-100) */}
          <Route
            path="/admin"
            element={
              <RoleGate role="admin">
                <AdminDashboardPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RoleGate role="admin">
                <UserManagementPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/users/new"
            element={
              <RoleGate role="admin">
                <AddUserPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <RoleGate role="admin">
                <EditUserPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <RoleGate role="admin">
                <DepartmentManagementPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/policies"
            element={
              <RoleGate role="admin">
                <PolicyManagementPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/templates"
            element={
              <RoleGate role="admin">
                <TemplateManagementPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/documents"
            element={
              <RoleGate role="admin">
                <DocumentManagementPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/access-requests"
            element={
              <RoleGate role="admin">
                <AccessRequestsPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RoleGate role="admin">
                <OrganisationSettingsPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RoleGate role="admin">
                <AuditLogPage />
              </RoleGate>
            }
          />
          <Route
            path="/admin/errors"
            element={
              <RoleGate role="admin">
                <ErrorDashboardPage />
              </RoleGate>
            }
          />
        </Route>

        {/* ============================================
            PLATFORM ADMIN ROUTES - cross-organisation Super Admin / Co-Admin
            area, entirely separate from the org-scoped routes above
            ============================================ */}
        <Route
          element={
            <PlatformProtectedRoute>
              <PlatformShell />
            </PlatformProtectedRoute>
          }
        >
          <Route path="/platform" element={<PlatformDashboardPage />} />
          <Route path="/platform/organisations" element={<OrganisationsListPage />} />
          <Route path="/platform/organisations/new" element={<AddOrganisationPage />} />
          <Route path="/platform/organisations/:id/invite-admin" element={<InviteOrgAdminPage />} />
          <Route
            path="/platform/admins"
            element={
              <PlatformRoleGate role="super_admin">
                <AdminsManagementPage />
              </PlatformRoleGate>
            }
          />
        </Route>

        {/* ============================================
            404 - Page Not Found
            ============================================ */}
        <Route path="*" element={<ComingSoonPage title="Page Not Found" />} />
      </Routes>
    </Suspense>
  );
}

export default App;
